import { useEffect, useRef, useState } from "react";
import { Wand2, Play, Pause, Download, Clapperboard, Loader2, ShoppingCart, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, streamUrl, mediaUrl } from "@/lib/api";
import { audio as globalAudio } from "@/store/player";
import { useAuth } from "@/context/AuthContext";
import { hubByKey } from "@/lib/hubs";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Header } from "@/pages/dashboard/Insights";
import { hdDownload } from "@/pages/dashboard/TrackManager";

const PRESETS = { warm: [3, -1, -18, 3, 1.25], balanced: [1.5, 1.5, -20, 3, 1.35], punchy: [2, 3, -24, 4, 1.55], loud: [2, 2.5, -28, 6, 1.85] };

const useMyTracks = () => {
  const [tracks, setTracks] = useState([]);
  useEffect(() => { api.get("/me/tracks").then((r) => setTracks(r.data.filter((t) => t.status === "active"))); }, []);
  return tracks;
};

const TrackSelect = ({ tracks, value, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className="bg-white/5 border-white/10 h-12"><SelectValue placeholder="Choose a track" /></SelectTrigger>
    <SelectContent className="bg-[#12121A] border-white/10">{tracks.map((t) => <SelectItem key={t.track_id} value={t.track_id}>{t.title}</SelectItem>)}</SelectContent>
  </Select>
);

export const Mastering = () => {
  const { user, refresh } = useAuth();
  const tracks = useMyTracks();
  const [id, setId] = useState("");
  const [preset, setPreset] = useState("balanced");
  const [intensity, setIntensity] = useState(60);
  const [enhanced, setEnhanced] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const g = useRef({});
  useEffect(() => () => { g.current.el?.pause(); g.current.ctx?.close(); }, []);
  useEffect(() => {
    const n = g.current;
    if (!n.ctx) return;
    const [lo, hi, th, ra, gain] = PRESETS[preset];
    const k = intensity / 100;
    n.low.gain.value = lo * k; n.high.gain.value = hi * k; n.comp.threshold.value = th * k; n.comp.ratio.value = 1 + (ra - 1) * k; n.out.gain.value = 1 + (gain - 1) * k;
    n.src.disconnect();
    n.src.connect(enhanced ? n.low : n.ctx.destination);
  }, [preset, intensity, enhanced, playing]);
  const toggle = () => {
    const n = g.current;
    if (!id) return toast.error("Choose a track first");
    if (!n.ctx || n.id !== id) {
      n.el?.pause(); n.ctx?.close();
      const el = new Audio(streamUrl(id));
      const ctx = new AudioContext();
      const src = ctx.createMediaElementSource(el);
      const low = ctx.createBiquadFilter(); low.type = "lowshelf"; low.frequency.value = 120;
      const high = ctx.createBiquadFilter(); high.type = "highshelf"; high.frequency.value = 8000;
      const comp = ctx.createDynamicsCompressor(); comp.attack.value = 0.005; comp.release.value = 0.2;
      const out = ctx.createGain();
      low.connect(high).connect(comp).connect(out).connect(ctx.destination);
      Object.assign(n, { el, ctx, src, low, high, comp, out, id });
    }
    globalAudio.pause();
    if (n.el.paused) { n.el.play(); setPlaying(true); } else { n.el.pause(); setPlaying(false); }
  };
  const apply = async () => {
    setBusy(true);
    try { await api.post(`/tracks/${id}/master`, { preset, intensity }); toast.success("Master rendered. HD download ready."); refresh(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const buy = async () => {
    try { const { data } = await api.post("/payments/checkout", { kind: "credit", quantity: 1, origin_url: window.location.origin }); window.location.href = data.checkout_url; } catch (e) { toast.error(errMsg(e)); }
  };
  const credits = user.plan === "elite" ? "Unlimited" : user.mastering_credits || 0;
  return (
    <div data-testid="smartmastering-module">
      <Header eyebrow="Studio" title="SmartMastering™">
        <div className="flex items-center gap-3"><span className="font-mono text-sm" data-testid="mastering-credits">Credits: {credits}</span>
          {user.plan !== "elite" && <button data-testid="mastering-buy-credit" onClick={buy} className="h-10 px-4 rounded-full border border-white/15 text-sm inline-flex items-center gap-2"><ShoppingCart size={14} /> Buy credit · $4.99</button>}</div>
      </Header>
      <div className="surface p-6 space-y-6 max-w-3xl">
        <TrackSelect tracks={tracks} value={id} onChange={(v) => { setId(v); g.current.el?.pause(); setPlaying(false); }} testId="mastering-track-select" />
        <div className="grid grid-cols-4 gap-2">{Object.keys(PRESETS).map((p) => <button key={p} data-testid={`mastering-preset-${p}`} onClick={() => setPreset(p)} className={`h-12 rounded-xl border capitalize font-semibold ${preset === p ? "bg-[var(--hub)] border-transparent" : "border-white/10"}`}>{p}</button>)}</div>
        <div><div className="flex justify-between text-sm mb-2"><span>Intensity</span><span className="font-mono">{intensity}%</span></div><Slider data-testid="mastering-intensity" value={[intensity]} max={100} step={1} onValueChange={([v]) => setIntensity(v)} /></div>
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="mastering-preview-button" onClick={toggle} className="h-11 px-5 rounded-full bg-white text-black font-semibold inline-flex items-center gap-2">{playing ? <Pause size={16} /> : <Play size={16} />} Instant preview</button>
          <div className="inline-flex p-1 rounded-full bg-white/5 border border-white/10" data-testid="mastering-ab-toggle">
            {[[false, "Original"], [true, "Enhanced"]].map(([v, l]) => <button key={l} onClick={() => setEnhanced(v)} className={`px-4 h-9 rounded-full text-sm ${enhanced === v ? "bg-white text-black" : "text-zinc-400"}`}>{l}</button>)}
          </div>
        </div>
        <div className="flex flex-wrap gap-3 pt-2 border-t border-white/5">
          <button data-testid="mastering-apply-button" disabled={!id || busy} onClick={apply} className="h-11 px-6 rounded-full bg-[var(--hub)] text-white font-semibold disabled:opacity-40 inline-flex items-center gap-2">{busy ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />} Render master (1 credit)</button>
          <button data-testid="mastering-hd-download" disabled={!id || !tracks.find((t) => t.track_id === id)?.mastered} onClick={() => hdDownload(id)} className="h-11 px-5 rounded-full border border-white/15 inline-flex items-center gap-2 disabled:opacity-40"><Download size={16} /> HD download</button>
        </div>
        <p className="text-xs text-zinc-500">Preview runs a real-time EQ + multiband-style compression chain in your browser. HD files are served via short-lived signed links.</p>
      </div>
    </div>
  );
};

export const PromoClip = () => {
  const tracks = useMyTracks();
  const [id, setId] = useState("");
  const [len, setLen] = useState(15);
  const [start, setStart] = useState(30);
  const [state, setState] = useState("idle");
  const [url, setUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const canvas = useRef(null);
  const file = blob ? new File([blob], "soundra-promo.mp4", { type: "video/mp4" }) : null;
  const canShare = !!(file && navigator.canShare && navigator.canShare({ files: [file] }));
  const share = async () => {
    try { await navigator.share({ files: [file], title: "Soundra promo clip" }); } catch (e) { if (e?.name !== "AbortError") toast.error("Sharing failed — use Download instead"); }
  };
  const generate = async () => {
    const t = tracks.find((x) => x.track_id === id);
    if (!t) return toast.error("Choose a track");
    const cv = canvas.current;
    if (!cv?.captureStream || typeof window.MediaRecorder === "undefined") return toast.error("Your browser can't record video. Please update your browser (iOS 14.3+ / Android Chrome).");
    // Create audio graph synchronously inside the tap gesture (required on iOS/Android).
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const el = new Audio();
    el.preload = "auto";
    el.playsInline = true;
    el.setAttribute("playsinline", "");
    el.src = streamUrl(id);
    const src = ctx.createMediaElementSource(el);
    const an = ctx.createAnalyser(); an.fftSize = 256;
    const dest = ctx.createMediaStreamDestination();
    src.connect(an); an.connect(dest);
    const unlock = el.play().catch(() => null);
    ctx.resume?.();
    setState("recording"); setUrl(null); setBlob(null);
    globalAudio.pause();
    const c = cv.getContext("2d");
    const color = hubByKey(t.genres[0])?.color || "#FF3366";
    const img = new Image();
    img.crossOrigin = "anonymous";
    const imgOk = await new Promise((res) => { img.onload = () => res(true); img.onerror = () => res(false); img.src = mediaUrl(t.cover_url) || ""; if (!t.cover_url) res(false); });
    await unlock;
    el.pause();
    await new Promise((res) => {
      const go = () => { el.currentTime = Math.min(start, Math.max(0, (el.duration || start + len) - len)); res(); };
      if (el.readyState >= 1) go(); else el.addEventListener("loadedmetadata", go, { once: true });
      setTimeout(res, 8000);
    });
    const stream = new MediaStream([...cv.captureStream(30).getVideoTracks(), ...dest.stream.getAudioTracks()]);
    const canCheck = typeof MediaRecorder.isTypeSupported === "function";
    const mime = ["video/mp4;codecs=avc1.42E01E,mp4a.40.2", "video/mp4;codecs=avc1,mp4a", "video/mp4", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"].find((m) => canCheck && MediaRecorder.isTypeSupported(m)) || "";
    const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    const chunks = [];
    rec.ondataavailable = (e) => chunks.push(e.data);
    rec.onstop = async () => {
      ctx.close();
      const type = (rec.mimeType || mime || "video/mp4").split(";")[0];
      const raw = new Blob(chunks, { type });
      setState("converting");
      try {
        const fd = new FormData();
        fd.append("file", raw, type.includes("mp4") ? "clip.mp4" : "clip.webm");
        const { data } = await api.post("/promo/convert", fd, { responseType: "blob" });
        const mp4 = new Blob([data], { type: "video/mp4" });
        setBlob(mp4);
        setUrl(URL.createObjectURL(mp4));
        setState("done");
      } catch (e) { toast.error("MP4 conversion failed, please try again"); setState("idle"); }
    };
    const bins = new Uint8Array(an.frequencyBinCount);
    const W = cv.width, H = cv.height;
    let raf; let t0 = 0; const s0 = el.currentTime;
    const draw = () => {
      an.getByteFrequencyData(bins);
      const p = Math.min(1, Math.max((el.currentTime - s0) / len, (performance.now() - t0) / (len * 1000 + 15000)));
      c.fillStyle = "#0A0A0F"; c.fillRect(0, 0, W, H);
      const grd = c.createRadialGradient(W / 2, H * 0.35, 20, W / 2, H * 0.35, W);
      grd.addColorStop(0, color + "88"); grd.addColorStop(1, "transparent"); c.fillStyle = grd; c.fillRect(0, 0, W, H);
      if (imgOk) { const s = W * 0.7; c.save(); c.beginPath(); if (c.roundRect) c.roundRect((W - s) / 2, H * 0.12, s, s, 28); else c.rect((W - s) / 2, H * 0.12, s, s); c.clip(); c.drawImage(img, (W - s) / 2, H * 0.12, s, s); c.restore(); }
      c.fillStyle = "#fff"; c.font = "800 44px 'Cabinet Grotesk', sans-serif"; c.textAlign = "center"; c.fillText(t.title, W / 2, H * 0.66);
      c.fillStyle = "rgba(255,255,255,0.7)"; c.font = "500 24px 'Plus Jakarta Sans', sans-serif"; c.fillText("Listen now on Soundra", W / 2, H * 0.70);
      const n = 48, bw = (W - 80) / n;
      for (let i = 0; i < n; i++) { const v = bins[Math.floor(i * bins.length / n * 0.7)] / 255; const h = 10 + v * 160; c.fillStyle = i / n < p ? color : "rgba(255,255,255,0.35)"; c.fillRect(40 + i * bw, H * 0.84 - h / 2, bw - 4, h); }
      c.fillStyle = "rgba(255,255,255,0.5)"; c.font = "600 20px 'JetBrains Mono', monospace"; c.fillText("soundra", W / 2, H - 40);
      if (p < 1 && !el.ended) raf = requestAnimationFrame(draw); else { if (rec.state !== "inactive") rec.stop(); el.pause(); }
    };
    try {
      await ctx.resume?.();
      await el.play();
      rec.start(1000); t0 = performance.now(); draw();
    } catch (e) { setState("idle"); cancelAnimationFrame(raf); ctx.close(); toast.error("Could not start rendering. Tap Generate again."); }
  };
  return (
    <div data-testid="promo-clip-generator">
      <Header eyebrow="Studio" title="Promo clip generator" />
      <div className="grid lg:grid-cols-[1fr_300px] gap-6">
        <div className="surface p-6 space-y-5">
          <TrackSelect tracks={tracks} value={id} onChange={setId} testId="promo-track-select" />
          <div className="flex gap-2">{[15, 30].map((l) => <button key={l} data-testid={`promo-length-${l}`} onClick={() => setLen(l)} className={`px-4 h-10 rounded-full border text-sm ${len === l ? "bg-white text-black" : "border-white/10"}`}>{l}s teaser</button>)}<span className="ml-auto text-xs font-mono text-zinc-500 self-center">9:16 · TikTok / Reels / Shorts</span></div>
          <div><div className="flex justify-between text-sm mb-2"><span>Start at</span><span className="font-mono">{start}s</span></div><Slider data-testid="promo-start-slider" value={[start]} max={240} step={1} onValueChange={([v]) => setStart(v)} /></div>
          <button data-testid="promo-generate-button" disabled={!id || state === "recording" || state === "converting"} onClick={generate} className="h-12 px-6 rounded-full bg-[var(--hub)] text-white font-semibold disabled:opacity-40 inline-flex items-center gap-2">{state === "converting" ? <><Loader2 className="animate-spin" size={16} /> Converting to MP4…</> : state === "recording" ? <><Loader2 className="animate-spin" size={16} /> Rendering {len}s…</> : <><Clapperboard size={16} /> Generate clip</>}</button>
          {url && <a data-testid="promo-download-link" href={url} download="soundra-promo.mp4" className="ml-3 h-12 px-6 rounded-full bg-white text-black font-semibold inline-flex items-center gap-2"><Download size={16} /> Download</a>}
          {url && canShare && <button data-testid="promo-share-button" onClick={share} className="ml-3 mt-3 sm:mt-0 h-12 px-6 rounded-full border border-white/20 font-semibold inline-flex items-center gap-2"><Share2 size={16} /> Save / Share</button>}
        </div>
        <div className="rounded-3xl overflow-hidden border border-white/10 bg-black aspect-[9/16]">
          {url ? <video data-testid="promo-preview-video" src={url} controls playsInline className="w-full h-full object-cover" /> : <canvas ref={canvas} width={540} height={960} className="w-full h-full" />}
        </div>
      </div>
      {url && <canvas ref={canvas} width={540} height={960} className="hidden" />}
    </div>
  );
};
